import React, { useReducer, useEffect, useState, useContext } from "react";
import { jwtDecode } from "jwt-decode";
import authReducer, { initialState } from "./Auth.reducer";
import API from "../../Utils/API";
import { setCurrentUser, clearCurrentUser, setUserData } from "./Auth.actions";
import { CircularProgress, Box } from "@mui/material";
import { fetchUserData } from "./AuthService";

const AuthContext = React.createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
   const [userState, dispatch] = useReducer(authReducer, initialState);
   const [loading, setLoading] = useState(true);
   const [tokenRefreshTimer, setTokenRefreshTimer] = useState(null);

   // Function to check token expiration time and set up refresh timer
   const setupTokenRefresh = (token) => {
      try {
         const decodedToken = jwtDecode(token);
         const expiryTime = decodedToken.exp * 1000; // Convert to milliseconds
         const currentTime = Date.now();

         // Calculate time until token expires (with 5-minute buffer)
         const timeUntilExpiry = expiryTime - currentTime - 5 * 60 * 1000;

         if (timeUntilExpiry <= 0) {
            // Token already expired or about to expire
            handleLogout();
            return;
         }

         // Clear any existing timer
         if (tokenRefreshTimer) {
            clearTimeout(tokenRefreshTimer);
         }

         // Set timer to refresh token before it expires
         const timerId = setTimeout(() => {
            refreshToken();
         }, timeUntilExpiry);

         setTokenRefreshTimer(timerId);
      } catch (error) {
         console.error("Token refresh setup error:", error);
         handleLogout();
      }
   };

   // Function to refresh the token
   const refreshToken = async () => {
      try {
         const token = localStorage.getItem("authToken");
         if (!token) {
            handleLogout();
            return;
         }

         const response = await API({
            method: "POST",
            url: "/refreshToken",
            headers: {
               Authorization: `Bearer ${token}`,
            },
         });

         if (response.status === 200 && response.data.token) {
            localStorage.setItem("authToken", response.data.token);
            setupTokenRefresh(response.data.token);

            // Update user state with new token
            const decodedToken = jwtDecode(response.data.token);
            dispatch(
               setCurrentUser(response.data.token, {
                  ...userState.user,
                  ...decodedToken,
               })
            );
         } else {
            handleLogout();
         }
      } catch (error) {
         console.error("Token refresh error:", error);
         handleLogout();
      }
   };

   const handleLogout = () => {
      localStorage.removeItem("authToken");
      dispatch(clearCurrentUser());

      // Clear token refresh timer
      if (tokenRefreshTimer) {
         clearTimeout(tokenRefreshTimer);
         setTokenRefreshTimer(null);
      }
   };

   const checkAuthentication = async () => {
      const token = localStorage.getItem("authToken");

      if (!token) {
         dispatch(clearCurrentUser());
         setLoading(false);
         return;
      }

      try {
         // First verify if token is valid JWT
         const decodedToken = jwtDecode(token);
         const currentTime = Date.now() / 1000;

         if (decodedToken.exp < currentTime) {
            // Token is expired
            localStorage.removeItem("authToken");
            dispatch(clearCurrentUser());
            setLoading(false);
            return;
         }

         // Set up token refresh timer
         setupTokenRefresh(token);

         // Verify token with backend
         const response = await API({
            method: "GET",
            url: "/verifyToken",
            headers: {
               Authorization: `Bearer ${token}`,
            },
         });

         if (response.status === 201 && response.data) {
            // Combine decoded token data with user record data
            const userData = {
               ...decodedToken,
               ...response.data,
            };

            if (response.data.role === "user") {
               const user = await fetchUserData(response.data.id);
               dispatch(setUserData(user.data));
            }
            dispatch(setCurrentUser(token, userData));
         } else {
            handleLogout();
         }
      } catch (error) {
         console.error("Authentication error:", error);
         // Only remove token if it's an auth error
         if (error.response?.status === 401 || error.response?.status === 403) {
            handleLogout();
         }
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      checkAuthentication();

      // Clean up token refresh timer on unmount
      return () => {
         if (tokenRefreshTimer) {
            clearTimeout(tokenRefreshTimer);
         }
      };
   }, []);

   if (loading) {
      return (
         <Box
            sx={{
               display: "flex",
               justifyContent: "center",
               alignItems: "center",
               height: "100dvh",
               width: "100%",
            }}
         >
            <CircularProgress />
         </Box>
      );
   }

   const contextValue = {
      userState,
      dispatch,
      checkAuthentication,
      logout: handleLogout,
   };

   return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export default AuthContext;
